import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Switch,
  Alert,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../../store';
import {
  selectNotificationOptimization,
  selectNotificationLoading,
  updateNotificationOptimization,
  optimizeNotificationTiming,
  updateQuietHours,
  updateChannelPreference,
} from '../../store/slices/notificationIntelligenceSlice';
import type { QuietHours, ChannelPreference } from '@coffeeplexity/shared';
import { styles } from './NotificationPreferencesPanel.styles';

interface NotificationPreferencesPanelProps {
  userId: string;
  onClose: () => void;
}

export const NotificationPreferencesPanel: React.FC<
  NotificationPreferencesPanelProps
> = ({ userId, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const optimization = useSelector(selectNotificationOptimization);
  const loading = useSelector(selectNotificationLoading);

  // Local state for preferences
  const [quietHours, setQuietHours] = useState<QuietHours>(
    optimization?.quietHours || {
      enabled: false,
      startTime: '22:00',
      endTime: '08:00',
      timezone: 'UTC',
    }
  );

  const [frequencyLimit, setFrequencyLimit] = useState(
    optimization?.frequencyLimit || 5
  );

  const [engagementThreshold, setEngagementThreshold] = useState(
    optimization?.engagementThreshold || 0.3
  );

  const [adaptationRate, setAdaptationRate] = useState(
    optimization?.adaptationRate || 0.5
  );

  // Handle quiet hours toggle
  const handleQuietHoursToggle = useCallback(
    (enabled: boolean) => {
      const updatedQuietHours = { ...quietHours, enabled };
      setQuietHours(updatedQuietHours);
      dispatch(updateQuietHours(updatedQuietHours));
    },
    [dispatch, quietHours]
  );

  // Handle channel preference toggle
  const handleChannelToggle = useCallback(
    (channel: 'push' | 'email' | 'in_app', enabled: boolean) => {
      const channelPref: ChannelPreference = {
        channel,
        enabled,
        priority: enabled ? 1 : 0,
        types: ['all'], // Default to all types
      };
      dispatch(updateChannelPreference(channelPref));
    },
    [dispatch]
  );

  // Handle frequency limit change
  const handleFrequencyChange = useCallback((change: number) => {
    setFrequencyLimit(prev => Math.max(1, Math.min(20, prev + change)));
  }, []);

  // Handle threshold changes
  const handleEngagementThresholdChange = useCallback((change: number) => {
    setEngagementThreshold(prev => Math.max(0.1, Math.min(1.0, prev + change)));
  }, []);

  const handleAdaptationRateChange = useCallback((change: number) => {
    setAdaptationRate(prev => Math.max(0.1, Math.min(1.0, prev + change)));
  }, []);

  // Save preferences
  const handleSavePreferences = useCallback(async () => {
    try {
      await dispatch(
        updateNotificationOptimization({
          userId,
          frequencyLimit,
          engagementThreshold,
          adaptationRate,
          quietHours,
          lastOptimized: new Date().toISOString(),
        })
      ).unwrap();

      Alert.alert('Success', 'Your notification preferences have been saved!');
      onClose();
    } catch (error) {
      Alert.alert('Error', 'Failed to save preferences. Please try again.');
    }
  }, [
    dispatch,
    userId,
    frequencyLimit,
    engagementThreshold,
    adaptationRate,
    quietHours,
    onClose,
  ]);

  // Trigger AI optimization
  const handleOptimizeNow = useCallback(async () => {
    try {
      await dispatch(optimizeNotificationTiming(userId)).unwrap();
      Alert.alert(
        'Optimization Complete',
        'Your notification timing has been optimized based on your recent activity!'
      );
    } catch (error) {
      Alert.alert(
        'Error',
        'Failed to optimize notifications. Please try again.'
      );
    }
  }, [dispatch, userId]);

  // Get channel preference status
  const getChannelEnabled = (channel: 'push' | 'email' | 'in_app') => {
    return (
      optimization?.channelPreferences.find(pref => pref.channel === channel)
        ?.enabled || false
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.overlay}>
        <View style={styles.modal}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Notification Preferences</Text>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Text style={styles.closeButtonText}>×</Text>
            </TouchableOpacity>
          </View>

          <ScrollView
            style={styles.content}
            showsVerticalScrollIndicator={false}
          >
            {/* Quiet Hours Section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>🔕 Quiet Hours</Text>
              <Text style={styles.sectionDescription}>
                Automatically pause notifications during your quiet hours
              </Text>
              <View style={styles.settingRow}>
                <Text style={styles.settingLabel}>Enable Quiet Hours</Text>
                <Switch
                  value={quietHours.enabled}
                  onValueChange={handleQuietHoursToggle}
                  trackColor={{ false: '#E9ECEF', true: '#3498DB' }}
                  thumbColor={quietHours.enabled ? '#FFFFFF' : '#FFFFFF'}
                />
              </View>
              {quietHours.enabled && (
                <View style={styles.quietHoursDetails}>
                  <Text style={styles.quietHoursText}>
                    {quietHours.startTime} - {quietHours.endTime}
                  </Text>
                  <Text style={styles.quietHoursNote}>
                    Only urgent notifications will be delivered during this time
                  </Text>
                </View>
              )}
            </View>

            {/* Channel Preferences */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>📱 Notification Channels</Text>
              <Text style={styles.sectionDescription}>
                Choose how you want to receive notifications
              </Text>

              <View style={styles.channelsList}>
                <View style={styles.settingRow}>
                  <View style={styles.channelInfo}>
                    <Text style={styles.settingLabel}>Push Notifications</Text>
                    <Text style={styles.channelDescription}>
                      Instant alerts on your device
                    </Text>
                  </View>
                  <Switch
                    value={getChannelEnabled('push')}
                    onValueChange={enabled =>
                      handleChannelToggle('push', enabled)
                    }
                    trackColor={{ false: '#E9ECEF', true: '#3498DB' }}
                    thumbColor='#FFFFFF'
                  />
                </View>

                <View style={styles.settingRow}>
                  <View style={styles.channelInfo}>
                    <Text style={styles.settingLabel}>
                      In-App Notifications
                    </Text>
                    <Text style={styles.channelDescription}>
                      Notifications within the app
                    </Text>
                  </View>
                  <Switch
                    value={getChannelEnabled('in_app')}
                    onValueChange={enabled =>
                      handleChannelToggle('in_app', enabled)
                    }
                    trackColor={{ false: '#E9ECEF', true: '#3498DB' }}
                    thumbColor='#FFFFFF'
                  />
                </View>

                <View style={styles.settingRow}>
                  <View style={styles.channelInfo}>
                    <Text style={styles.settingLabel}>Email Notifications</Text>
                    <Text style={styles.channelDescription}>
                      Weekly summaries and important updates
                    </Text>
                  </View>
                  <Switch
                    value={getChannelEnabled('email')}
                    onValueChange={enabled =>
                      handleChannelToggle('email', enabled)
                    }
                    trackColor={{ false: '#E9ECEF', true: '#3498DB' }}
                    thumbColor='#FFFFFF'
                  />
                </View>
              </View>
            </View>

            {/* Frequency Control */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>📊 Frequency Control</Text>
              <Text style={styles.sectionDescription}>
                Maximum notifications per day
              </Text>
              <View style={styles.frequencyControl}>
                <TouchableOpacity
                  style={styles.frequencyButton}
                  onPress={() => handleFrequencyChange(-1)}
                >
                  <Text style={styles.frequencyButtonText}>−</Text>
                </TouchableOpacity>
                <Text style={styles.frequencyValue}>{frequencyLimit}</Text>
                <TouchableOpacity
                  style={styles.frequencyButton}
                  onPress={() => handleFrequencyChange(1)}
                >
                  <Text style={styles.frequencyButtonText}>+</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* AI Learning Settings */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>🤖 AI Learning</Text>
              <Text style={styles.sectionDescription}>
                Control how aggressively the AI adapts to your preferences
              </Text>

              <View style={styles.aiSettingRow}>
                <Text style={styles.aiSettingLabel}>Engagement Threshold</Text>
                <Text style={styles.aiSettingDescription}>
                  Minimum engagement rate for notifications
                </Text>
                <View style={styles.thresholdControl}>
                  <TouchableOpacity
                    style={styles.thresholdButton}
                    onPress={() => handleEngagementThresholdChange(-0.1)}
                  >
                    <Text style={styles.thresholdButtonText}>−</Text>
                  </TouchableOpacity>
                  <Text style={styles.thresholdValue}>
                    {Math.round(engagementThreshold * 100)}%
                  </Text>
                  <TouchableOpacity
                    style={styles.thresholdButton}
                    onPress={() => handleEngagementThresholdChange(0.1)}
                  >
                    <Text style={styles.thresholdButtonText}>+</Text>
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.aiSettingRow}>
                <Text style={styles.aiSettingLabel}>Adaptation Speed</Text>
                <Text style={styles.aiSettingDescription}>
                  How quickly AI adapts to your behavior changes
                </Text>
                <View style={styles.thresholdControl}>
                  <TouchableOpacity
                    style={styles.thresholdButton}
                    onPress={() => handleAdaptationRateChange(-0.1)}
                  >
                    <Text style={styles.thresholdButtonText}>−</Text>
                  </TouchableOpacity>
                  <Text style={styles.thresholdValue}>
                    {Math.round(adaptationRate * 100)}%
                  </Text>
                  <TouchableOpacity
                    style={styles.thresholdButton}
                    onPress={() => handleAdaptationRateChange(0.1)}
                  >
                    <Text style={styles.thresholdButtonText}>+</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            {/* AI Optimization */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>⚡ AI Optimization</Text>
              <Text style={styles.sectionDescription}>
                Let AI analyze your recent activity and optimize notification
                timing
              </Text>
              <TouchableOpacity
                style={[
                  styles.optimizeButton,
                  loading.optimization && styles.optimizeButtonDisabled,
                ]}
                onPress={handleOptimizeNow}
                disabled={loading.optimization}
              >
                <Text style={styles.optimizeButtonText}>
                  {loading.optimization ? 'Optimizing...' : 'Optimize Now'}
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.saveButton,
                loading.optimization && styles.saveButtonDisabled,
              ]}
              onPress={handleSavePreferences}
              disabled={loading.optimization}
            >
              <Text style={styles.saveButtonText}>Save Preferences</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
};
